import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import {
  confirmSocTripwirePlaced,
  downloadDecoyDoc,
  getSocSources,
  getSocTripwire,
  openSocTripwireTest,
  placeTripwire,
  type SocSources,
  type SocTripwire,
} from '../lib/api'
import {
  CouldNotSee,
  SocShell,
  btn,
  btnBrass,
  btnGhost,
  card,
  h2Class,
  kicker,
  linkClass,
  routeError,
  when,
} from '../components/soc/SocShell'
import { filesPlace } from '../components/soc/OwnerHome'
import { cn } from '../lib/utils'
import { businessTime } from '../lib/businessTime'

// /client/tripwires (BRIEF-E2b; docs/design/customer-page/Tripwires.dc.html):
// name the document, download it, say where it was saved, confirm. The
// download goes through the existing canary routes (POST /canary/place, then
// the download_url it returns); the confirm posts the words typed to
// /soc/tripwires/{id}/placed; the table is /soc/sources' tripwires.
//
// BRIEF-E6: "Test it" on a row opens a ten-minute test window (POST
// /soc/tripwires/{id}/test) and polls that row (GET /soc/tripwires/{id})
// until a fire arrives inside the window or it expires. The page says a
// tripwire works only when the backend says a real fire arrived.
//
// BRIEF-E6b: "Test it" only where it can work. Not on a row the backend says
// is not testable (the onboarding baseline decoy nobody saved, or one still
// waiting to be saved), and not for anyone the route refuses (only the
// business's owner or admin may test). A poll that fails is counted: after
// three in a row the row says we cannot reach DigitalWatch, never that the
// tripwire works.

export const TEST_POLL_MS = 5000
export const TEST_WAIT_LINE =
  'Open the document you saved, from where you saved it, now. We will wait 10 minutes.'
export const TEST_NOTHING_LINE =
  'Nothing arrived in 10 minutes. Check you opened the copy you saved here, and try again.'
export const TEST_UNREACHABLE_LINE =
  'We cannot reach DigitalWatch to check. Your test may still have worked; look again in a minute.'
/** Failed polls in a row before the row says we cannot reach DigitalWatch. */
export const TEST_FAILS_BEFORE_SAYING = 3
export const TEST_UNSAVED_LINE = 'Save it and tell us where, then you can test it.'
export const TEST_NOT_YOURS_LINE = 'We placed this one, not you, so there is no copy of yours to test.'
export const TEST_OWNER_ONLY_LINE = 'Only the owner or an admin of the business can test a tripwire.'

/** A row as /soc/sources sends it; `testable` is absent from a backend older
 *  than E6b, and the page then offers no test. */
type Row = SocTripwire & { testable?: boolean }

/** "It fired at 14:15 (your time) from Auckland. Your tripwire works.", with
 *  the day ("at 14:15 on Wed 28 Oct") when it was not today on the
 *  business's clock, so an old result never reads like this morning's. */
export function firedLine(test: NonNullable<SocTripwire['test']>, now: Date = new Date()): string {
  const time = when(test.fired_at, 'HH:mm')
  const day = when(test.fired_at, 'yyyy-MM-dd')
  const at = time
    ? day && day !== businessTime(now, 'yyyy-MM-dd')
      ? `${time} on ${when(test.fired_at, 'EEE d MMM')}`
      : time
    : 'a time we could not read'
  const from = test.fired_from ? ` from ${test.fired_from}` : ''
  return `It fired at ${at} (your time)${from}. Your tripwire works.`
}

export const NAME_IDEAS = [
  'Staff salaries 2026.docx',
  'Passwords - office.docx',
  'Sale of business - draft terms.docx',
]

type Placed = {
  tokenId: string | null
  downloadUrl: string
  filename: string
  href: string | null
}

/** The tripwire's id, read from the download path the place route returned. */
export function tokenIdFrom(downloadUrl: string): string | null {
  const m = /\/canary\/download\/([0-9a-f-]{36})(?:[/?#]|$)/i.exec(downloadUrl)
  return m ? m[1] : null
}

function asDocx(name: string): string {
  return /\.docx$/i.test(name) ? name : `${name}.docx`
}

/** Tempting places, in the business's own cloud's words (BRIEF-E5d). */
export function tempting(sources: SocSources | null): string {
  const place = filesPlace(sources)
  return place
    ? `a finance folder, a director's ${place}, a shared folder`
    : 'a finance folder, a director\'s own files, a shared folder'
}

const stepDot =
  'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-navy text-[15px] font-bold text-cream'
const input =
  'box-border min-h-[44px] rounded-md border border-[#cfc5b3] bg-white px-3.5 py-3 text-base text-navy'

// Tripwires are the owner's (B18; the backend refuses a plain member's). A
// decoy works because nobody has a reason to open it, so a member never reads
// how they work. The menu hides the door; this closes it.
export function TripwiresPage() {
  const plainMember = useAuthStore((s) => s.principal?.plain_member)
  if (plainMember) return <Navigate to="/client" replace />
  return <TripwiresWorkspace />
}

function TripwiresWorkspace() {
  // The route's own rule (soc.py _owner_or_admin): never a button it refuses.
  const mayTest = useAuthStore(
    (s) => s.principal?.role === 'client' && (s.principal.org_role === 'owner' || s.principal.org_role === 'admin'),
  )
  const [sources, setSources] = useState<SocSources | null>(null)
  const [sourcesError, setSourcesError] = useState(false)
  const [name, setName] = useState('')
  const [placed, setPlaced] = useState<Placed | null>(null)
  const [placing, setPlacing] = useState(false)
  const [placeError, setPlaceError] = useState<string | null>(null)
  const [where, setWhere] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [confirmed, setConfirmed] = useState<string | null>(null)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [opening, setOpening] = useState<string | null>(null)
  const [testError, setTestError] = useState<Record<string, string>>({})
  const [pollFails, setPollFails] = useState<Record<string, number>>({})
  const rows = useRef<Row[]>([])
  rows.current = sources?.tripwires ?? []
  const fails = useRef<Record<string, number>>({})
  fails.current = pollFails
  const anchor = useRef<HTMLAnchorElement>(null)

  function loadSources() {
    getSocSources().then(
      (data) => {
        setSources(data)
        setSourcesError(false)
      },
      () => setSourcesError(true),
    )
  }

  useEffect(loadSources, [])

  /** One row, as the backend just returned it, into the table. */
  function patchRow(row: SocTripwire) {
    setSources((prev) =>
      prev ? { ...prev, tripwires: prev.tripwires.map((t) => (t.id === row.id ? { ...t, ...row } : t)) } : prev,
    )
  }

  async function startTest(id: string) {
    if (opening) return
    setOpening(id)
    setTestError((prev) => ({ ...prev, [id]: '' }))
    try {
      patchRow(await openSocTripwireTest(id))
    } catch (err) {
      setTestError((prev) => ({
        ...prev,
        [id]: routeError(err, 'We could not start the test just now. Please try again.'),
      }))
    } finally {
      setOpening(null)
    }
  }

  // While any window is open, ask the backend for that row every few seconds;
  // the backend alone says whether a fire arrived or the ten minutes passed.
  // A failed poll is counted, never swallowed. A row we cannot reach is asked
  // for until its window has ended and it already says so (a row we can
  // reach says expired itself on the first poll after the window ends), so
  // "We will wait" is never left standing on a dead connection.
  const waitingIds = (sources?.tripwires ?? []).filter((t) => t.test?.state === 'waiting').map((t) => t.id)
  const waitingKey = waitingIds.join(',')
  useEffect(() => {
    if (!waitingKey) return
    const ids = waitingKey.split(',')
    const timer = setInterval(() => {
      for (const id of ids) {
        const t = rows.current.find((r) => r.id === id)
        const ends = t?.test?.expires_at ? new Date(t.test.expires_at).getTime() : NaN
        if ((fails.current[id] ?? 0) >= TEST_FAILS_BEFORE_SAYING && !(Date.now() <= ends)) continue
        getSocTripwire(id).then(
          (row) => {
            setPollFails((prev) => ({ ...prev, [id]: 0 }))
            patchRow(row)
          },
          () => setPollFails((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 })),
        )
      }
    }, TEST_POLL_MS)
    return () => clearInterval(timer)
  }, [waitingKey])

  useEffect(() => {
    // The file is handed over once, the moment it is ready.
    if (placed?.href) anchor.current?.click()
  }, [placed?.href])

  useEffect(() => {
    const href = placed?.href
    return () => {
      if (href && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(href)
    }
  }, [placed?.href])

  async function download() {
    const chosen = name.trim()
    if (!chosen || placing || placed) return
    setPlacing(true)
    setPlaceError(null)
    try {
      const out = await placeTripwire('word_doc', undefined, chosen)
      if (!out.download_url) {
        setPlaceError('The tripwire was made, but no document came back to download.')
        return
      }
      const filename = asDocx(chosen)
      const blob = await downloadDecoyDoc(out.download_url, filename)
      const href = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null
      setPlaced({ tokenId: tokenIdFrom(out.download_url), downloadUrl: out.download_url, filename, href })
      loadSources()
    } catch (err) {
      setPlaceError(routeError(err, 'We could not make the document just now. Please try again.'))
    } finally {
      setPlacing(false)
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault()
    const words = where.trim()
    if (!placed?.tokenId || !words || confirming) return
    setConfirming(true)
    setConfirmError(null)
    try {
      const out = await confirmSocTripwirePlaced(placed.tokenId, words)
      setConfirmed(
        out.proof === 'pending'
          ? `Recorded: ${out.where ?? words}. It has not been proved yet; the table below says when it has.`
          : `Recorded: ${out.where ?? words}.`,
      )
      loadSources()
    } catch (err) {
      setConfirmError(routeError(err, 'We could not record where you saved it. Please try again.'))
    } finally {
      setConfirming(false)
    }
  }

  const tripwireGaps = (sources?.could_not_see ?? []).filter((line) => line.startsWith('tripwires'))

  function testCell(t: Row) {
    if (t.status === 'switched off') {
      return <span className="text-[#6b5a3e]">Switched off, so it cannot be tested.</span>
    }
    const test = t.test ?? null
    const error = testError[t.id]
    const unreachable = (pollFails[t.id] ?? 0) >= TEST_FAILS_BEFORE_SAYING
    // A button only where the route can say yes; else, where we know it, why not.
    const offer = t.testable === true && mayTest
    const cannot =
      t.status === 'waiting for you to save it'
        ? TEST_UNSAVED_LINE
        : t.testable === false
          ? TEST_NOT_YOURS_LINE
          : t.testable === true && !mayTest
            ? TEST_OWNER_ONLY_LINE
            : null
    const again = (label: string) =>
      !offer ? null : (
      <button
        type="button"
        className={cn(btnGhost, 'px-3 py-1.5 text-sm font-normal')}
        disabled={!!opening}
        onClick={() => void startTest(t.id)}
      >
        {label}
      </button>
    )
    return (
      <div className="flex flex-col gap-2">
        {test?.state === 'waiting' && (
          <p className="m-0 font-bold text-[#8a6830]" role="status">
            {unreachable ? TEST_UNREACHABLE_LINE : TEST_WAIT_LINE}
          </p>
        )}
        {test?.state === 'fired' && (
          <>
            <p className="m-0 font-bold text-[#2f6b3a]" role="status">
              {firedLine(test)}
            </p>
            {again('Test it again')}
          </>
        )}
        {test?.state === 'expired' && (
          <>
            <p className="m-0 text-[#6b5a3e]" role="status">
              {TEST_NOTHING_LINE}
            </p>
            {again('Try again')}
          </>
        )}
        {!test && again(opening === t.id ? 'Starting…' : 'Test it')}
        {!test && cannot && <span className="text-[#6b5a3e]">{cannot}</span>}
        {error && (
          <p className="m-0 text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <SocShell current="tripwires">
      <section aria-label="What a tripwire is" className="max-w-[760px]">
        <p className={cn(kicker, 'm-0 mb-2.5')}>Tripwires</p>
        <h1 className="m-0 mb-4 font-serif text-[30px] font-normal leading-tight sm:text-[40px]">
          A document nobody should open. If anyone does, your SOC is told.
        </h1>
        <p className="m-0 text-lg leading-relaxed text-[#2b3540]">
          A tripwire is an ordinary-looking Word document that we make and you save somewhere
          tempting: {tempting(sources)}. Nobody in the business
          has a reason to open it. An attacker reading your files does. We never see what else is in
          that folder, and we never place it ourselves: you choose the name and where it lives.
        </p>
      </section>

      <section aria-label="Place a tripwire" className={cn(card, 'flex flex-col gap-6 p-5 sm:p-7')}>
        <h2 className={h2Class}>Place a new tripwire</h2>

        <div className="flex flex-wrap items-start gap-4">
          <div className={stepDot} aria-hidden="true">1</div>
          <div className="flex min-w-0 flex-[999_1_420px] flex-col gap-2.5">
            <label htmlFor="docname" className="text-[17px] font-bold">
              Name the document the way you would name a real one
            </label>
            <p className="m-0 text-[15px] text-[#6b5a3e]">
              Something a thief would open and a colleague would walk past. Not "tripwire" or "canary".
            </p>
            <input
              id="docname"
              type="text"
              value={name}
              maxLength={150}
              disabled={!!placed}
              onChange={(e) => setName(e.target.value)}
              placeholder="Shareholder loan schedule FY26.docx"
              className={cn(input, 'w-full max-w-[560px]')}
            />
            <div className="flex flex-wrap items-center gap-2 text-sm text-[#6b5a3e]">
              <span>Ideas:</span>
              {NAME_IDEAS.map((idea) => (
                <button
                  key={idea}
                  type="button"
                  disabled={!!placed}
                  onClick={() => setName(idea)}
                  className={cn(btnGhost, 'px-3 py-1.5 text-sm font-normal')}
                >
                  {idea}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-4">
          <div className={stepDot} aria-hidden="true">2</div>
          <div className="flex min-w-0 flex-[999_1_420px] flex-col gap-2.5">
            <p className="m-0 text-[17px] font-bold">Download it</p>
            <p className="m-0 text-[15px] text-[#6b5a3e]">
              The file opens as a normal Word document. Do not edit or rename it after you save it.
            </p>
            <div>
              {placed ? (
                placed.href ? (
                  <a ref={anchor} href={placed.href} download={placed.filename} className={cn(linkClass, 'inline-flex min-h-[44px] items-center')}>
                    Download {placed.filename} again
                  </a>
                ) : (
                  <p className="m-0 text-[15px]">Your browser did not hand over the file; please try another browser.</p>
                )
              ) : (
                <button type="button" className={btn} disabled={!name.trim() || placing} onClick={() => void download()}>
                  {placing ? 'Making the document…' : 'Download the document'}
                </button>
              )}
            </div>
            {placeError && (
              <p className="m-0 text-[15px] text-danger" role="alert">
                {placeError}
              </p>
            )}
          </div>
        </div>

        <form onSubmit={confirm} className="flex flex-wrap items-start gap-4">
          <div className={stepDot} aria-hidden="true">3</div>
          <div className="flex min-w-0 flex-[999_1_420px] flex-col gap-2.5">
            <label htmlFor="where" className="text-[17px] font-bold">
              Tell us where you saved it
            </label>
            <p className="m-0 text-[15px] text-[#6b5a3e]">
              So that when it fires we can tell you which folder was reached. We do not look there ourselves.
            </p>
            <input
              id="where"
              type="text"
              value={where}
              maxLength={500}
              disabled={!placed || !!confirmed}
              onChange={(e) => setWhere(e.target.value)}
              placeholder="For example: Finance shared drive, folder 2026 / Directors"
              className={cn(input, 'w-full max-w-[560px]')}
            />
            {placed && !placed.tokenId && (
              <p className="m-0 text-[15px] text-danger" role="alert">
                We could not tell which tripwire this is, so where you saved it cannot be recorded here.
              </p>
            )}
            {!confirmed && (
              <div>
                <button
                  type="submit"
                  className={btnBrass}
                  disabled={!placed?.tokenId || !where.trim() || confirming}
                >
                  Confirm it is in place
                </button>
              </div>
            )}
            {confirmed && (
              <p className="m-0 text-[15px] font-bold text-[#8a6830]" role="status">
                {confirmed}
              </p>
            )}
            {confirmError && (
              <p className="m-0 text-[15px] text-danger" role="alert">
                {confirmError}
              </p>
            )}
          </div>
        </form>
      </section>

      <section aria-label="Your tripwires" className="flex flex-col gap-3.5">
        <h2 className={h2Class}>Your tripwires</h2>
        {sourcesError && (
          <p className="m-0 text-[15px] text-[#6b5a3e]" role="alert">
            We could not load your tripwires just now, so this shows nothing rather than a guess.
          </p>
        )}
        {!sources && !sourcesError && (
          <p className="m-0 text-[15px] text-[#6b5a3e]" role="status">
            Loading your tripwires…
          </p>
        )}
        {sources && sources.tripwires.length === 0 && (
          <p className="m-0 text-[15px] text-[#6b5a3e]">No tripwire is placed.</p>
        )}
        {sources && sources.tripwires.length > 0 && (
          <div className={cn(card, 'overflow-x-auto px-4 pb-2.5 pt-1.5 sm:px-6')} data-testid="tripwires-table">
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[.12em] text-[#6b5a3e]">
                  <th className="px-2.5 py-3 font-normal">Document</th>
                  <th className="px-2.5 py-3 font-normal">Where you put it</th>
                  <th className="px-2.5 py-3 font-normal">Placed</th>
                  <th className="px-2.5 py-3 font-normal">Proved</th>
                  <th className="px-2.5 py-3 font-normal">Status</th>
                  <th className="px-2.5 py-3 font-normal">Test</th>
                </tr>
              </thead>
              <tbody>
                {sources.tripwires.map((t) => (
                  <tr key={t.id} className="border-t border-[#efe9de] align-top text-[15px]">
                    <td className="px-2.5 py-3">
                      <strong>{t.name ?? 'A tripwire'}</strong>
                    </td>
                    <td className="px-2.5 py-3">{t.where ?? 'Not said yet'}</td>
                    <td className="px-2.5 py-3">{when(t.placed_at, 'd MMM yyyy') ?? 'Not recorded'}</td>
                    <td className="px-2.5 py-3">{when(t.proved_at) ?? 'Not yet'}</td>
                    <td className="px-2.5 py-3">
                      {t.status}
                      {t.last_fired_at ? `, last ${when(t.last_fired_at)}` : ''}
                    </td>
                    <td className="min-w-[260px] px-2.5 py-3" data-testid={`test-${t.id}`}>
                      {testCell(t)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <CouldNotSee items={tripwireGaps} />
        <p className="m-0 text-sm text-[#6b5a3e]">
          If a tripwire fires, your SOC treats it as an attack in progress, unless it is your own
          test from this page: press Test it, open the copy you saved, and watch it fire.
        </p>
      </section>
    </SocShell>
  )
}
